'use client';

import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["slots", "slotProps", "className", "classes", "sx", "children", "features", "variant", "disabled"];
import * as React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { ComposerRoot, useChatVariant } from '@mui/x-chat-headless';
import { styled, createUseThemeProps } from "../internals/zero-styled/index.mjs";
import { chatComposerClasses, useChatComposerUtilityClasses } from "./chatComposerClasses.mjs";
import { ChatComposerTextArea } from "./ChatComposerTextArea.mjs";
import { ChatComposerToolbar } from "./ChatComposerToolbar.mjs";
import { ChatComposerSendButton } from "./ChatComposerSendButton.mjs";
import { ChatComposerAttachButton } from "./ChatComposerAttachButton.mjs";
import { ChatComposerAttachmentList } from "./ChatComposerAttachmentList.mjs";
import DefaultSendIcon from "../icons/DefaultSendIcon.mjs";
import DefaultAttachIcon from "../icons/DefaultAttachIcon.mjs";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const useThemeProps = createUseThemeProps('MuiChatComposer');
const ChatComposerStyled = styled('form', {
  name: 'MuiChatComposer',
  slot: 'Root',
  overridesResolver: (props, styles) => [styles.root, props.ownerState?.disabled && styles.disabled]
})(({
  theme
}) => ({
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  zIndex: 1,
  gap: theme.spacing(0.5),
  padding: theme.spacing(1, 1.5),
  border: '1px solid',
  borderColor: (theme.vars || theme).palette.divider,
  borderRadius: (theme.vars || theme).shape.borderRadius,
  backgroundColor: (theme.vars || theme).palette.background.paper,
  boxSizing: 'border-box',
  flexShrink: 0,
  margin: theme.spacing(0, 1.5, 1.5),
  transition: theme.transitions.create(['border-color', 'box-shadow'], {
    duration: theme.transitions.duration.short
  }),
  '@media (prefers-reduced-motion: reduce)': {
    transition: 'none'
  },
  '&:focus-within:not([data-disabled])': {
    borderColor: (theme.vars || theme).palette.primary.main,
    boxShadow: `0 0 0 1px ${(theme.vars || theme).palette.primary.main}`
  },
  '&[data-disabled]': {
    backgroundColor: (theme.vars || theme).palette.action.disabledBackground,
    opacity: (theme.vars || theme).palette.action.disabledOpacity,
    pointerEvents: 'none'
  },
  [`&.${chatComposerClasses.variantCompact}`]: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    borderRadius: (theme.vars || theme).shape.borderRadius,
    backgroundColor: (theme.vars || theme).palette.background.paper,
    border: '1px solid',
    borderColor: (theme.vars || theme).palette.divider,
    margin: theme.spacing(0, 1, 1),
    padding: theme.spacing(0.5),
    boxShadow: 'none',
    '&:focus-within:not([data-disabled])': {
      borderColor: (theme.vars || theme).palette.primary.main,
      boxShadow: `0 0 0 1px ${(theme.vars || theme).palette.primary.main}`
    },
    [`& .${chatComposerClasses.attachmentList}`]: {
      flexBasis: '100%',
      order: -1
    },
    [`& .${chatComposerClasses.attachButton}`]: {
      borderRadius: '50%',
      width: 36,
      height: 36
    },
    [`& .${chatComposerClasses.sendButton}`]: {
      borderRadius: (theme.vars || theme).shape.borderRadius,
      width: 36,
      height: 36,
      padding: 0,
      backgroundColor: (theme.vars || theme).palette.primary.main,
      color: (theme.vars || theme).palette.primary.contrastText,
      '&:disabled': {
        backgroundColor: (theme.vars || theme).palette.primary.main,
        color: (theme.vars || theme).palette.primary.contrastText,
        opacity: (theme.vars || theme).palette.action.disabledOpacity,
        cursor: 'not-allowed'
      }
    }
  }
}));
const DefaultComposerContent = /*#__PURE__*/React.memo(function DefaultComposerContent({
  features
}) {
  const showAttachments = features?.attachments !== false;
  return /*#__PURE__*/_jsxs(React.Fragment, {
    children: [showAttachments && /*#__PURE__*/_jsx(ChatComposerAttachmentList, {}), /*#__PURE__*/_jsx(ChatComposerTextArea, {}), /*#__PURE__*/_jsxs(ChatComposerToolbar, {
      children: [showAttachments && /*#__PURE__*/_jsx(ChatComposerAttachButton, {
        children: /*#__PURE__*/_jsx(DefaultAttachIcon, {})
      }), /*#__PURE__*/_jsx(ChatComposerSendButton, {
        children: /*#__PURE__*/_jsx(DefaultSendIcon, {})
      })]
    })]
  });
});

// @ts-expect-error React.memo typing doesn't include propTypes
if (process.env.NODE_ENV !== "production") DefaultComposerContent.displayName = "DefaultComposerContent";
process.env.NODE_ENV !== "production" ? DefaultComposerContent.propTypes = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // | To update them edit the TypeScript types and run "pnpm proptypes"  |
  // ----------------------------------------------------------------------
  features: PropTypes.shape({
    attachments: PropTypes.oneOfType([PropTypes.shape({
      acceptedMimeTypes: PropTypes.arrayOf(PropTypes.string),
      maxFileCount: PropTypes.number,
      maxFileSize: PropTypes.number,
      onAttachmentReject: PropTypes.func
    }), PropTypes.bool])
  })
} : void 0;
const CompactComposerContent = /*#__PURE__*/React.memo(function CompactComposerContent({
  features
}) {
  const showAttachments = features?.attachments !== false;
  return /*#__PURE__*/_jsxs(React.Fragment, {
    children: [showAttachments && /*#__PURE__*/_jsx(ChatComposerAttachmentList, {}), showAttachments && /*#__PURE__*/_jsx(ChatComposerAttachButton, {
      children: /*#__PURE__*/_jsx(DefaultAttachIcon, {})
    }), /*#__PURE__*/_jsx(ChatComposerTextArea, {
      maxRows: 5
    }), /*#__PURE__*/_jsx(ChatComposerSendButton, {
      children: /*#__PURE__*/_jsx(DefaultSendIcon, {})
    })]
  });
});

// @ts-expect-error React.memo typing doesn't include propTypes
if (process.env.NODE_ENV !== "production") CompactComposerContent.displayName = "CompactComposerContent";
process.env.NODE_ENV !== "production" ? CompactComposerContent.propTypes = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // | To update them edit the TypeScript types and run "pnpm proptypes"  |
  // ----------------------------------------------------------------------
  features: PropTypes.shape({
    attachments: PropTypes.oneOfType([PropTypes.shape({
      acceptedMimeTypes: PropTypes.arrayOf(PropTypes.string),
      maxFileCount: PropTypes.number,
      maxFileSize: PropTypes.number,
      onAttachmentReject: PropTypes.func
    }), PropTypes.bool])
  })
} : void 0;
const ChatComposer = /*#__PURE__*/React.forwardRef(function ChatComposer(inProps, ref) {
  const props = useThemeProps({
    props: inProps,
    name: 'MuiChatComposer'
  });
  const {
      slots,
      slotProps,
      className,
      classes: classesProp,
      sx,
      children,
      features,
      variant: variantProp,
      disabled = false
    } = props,
    other = _objectWithoutPropertiesLoose(props, _excluded);
  const contextVariant = useChatVariant();
  const variant = variantProp ?? contextVariant;
  const isCompact = variant === 'compact';
  const classes = useChatComposerUtilityClasses(classesProp);
  const attachmentConfig = typeof features?.attachments === 'object' ? features.attachments : undefined;
  const defaultContent = isCompact ? /*#__PURE__*/_jsx(CompactComposerContent, {
    features: features
  }) : /*#__PURE__*/_jsx(DefaultComposerContent, {
    features: features
  });
  return /*#__PURE__*/_jsx(ComposerRoot, _extends({
    ref: ref,
    disabled: disabled
  }, other, {
    attachmentConfig: attachmentConfig,
    slots: _extends({
      root: slots?.root ?? ChatComposerStyled
    }, slots),
    slotProps: _extends({}, slotProps, {
      root: _extends({
        className: clsx(classes.root, className, isCompact && classes.variantCompact, disabled && classes.disabled),
        sx
      }, slotProps?.root)
    }),
    children: children ?? defaultContent
  }));
});
if (process.env.NODE_ENV !== "production") ChatComposer.displayName = "ChatComposer";
process.env.NODE_ENV !== "production" ? ChatComposer.propTypes = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // | To update them edit the TypeScript types and run "pnpm proptypes"  |
  // ----------------------------------------------------------------------
  /**
   * Configuration for attachment validation constraints.
   * When provided, file attachments are validated against these rules.
   */
  attachmentConfig: PropTypes.shape({
    acceptedMimeTypes: PropTypes.arrayOf(PropTypes.string),
    maxFileCount: PropTypes.number,
    maxFileSize: PropTypes.number,
    onAttachmentReject: PropTypes.func
  }),
  classes: PropTypes.object,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  /**
   * Feature flags to control composer capabilities.
   */
  features: PropTypes.shape({
    attachments: PropTypes.oneOfType([PropTypes.shape({
      acceptedMimeTypes: PropTypes.arrayOf(PropTypes.string),
      maxFileCount: PropTypes.number,
      maxFileSize: PropTypes.number,
      onAttachmentReject: PropTypes.func
    }), PropTypes.bool])
  }),
  /**
   * Handler invoked when the form is submitted.
   *
   * Native form submission is always prevented before this handler runs.
   * Call `event.preventDefault()` from inside the handler to also suppress the
   * composer's own `submit()` action; otherwise the composer submits as usual
   * after the handler returns.
   */
  onSubmit: PropTypes.func,
  slotProps: PropTypes.object,
  slots: PropTypes.object,
  sx: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])), PropTypes.func, PropTypes.object]),
  /**
   * The visual layout variant of the composer.
   * - `'default'` – Stacked layout: attachment list, textarea, then toolbar below.
   * - `'compact'` – Inline layout: start actions, textarea, end actions in a single row.
   *
   * When omitted, inherits from the nearest `ChatVariantProvider` (e.g. set by `ChatBox`).
   * @default 'default'
   */
  variant: PropTypes.oneOf(['compact', 'default'])
} : void 0;
export { ChatComposer };